/** @format */
/**
 * eslint-disable max-len
 */

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Chain, ProductSku } from '@shared/data-access/interfaces';
import { CookieService, KillswitchService, UtilityService } from '@shared/data-access/services';

import { ProductService } from '@shared/data-access/product';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import {
	changeHeaderServiceStoreInfo,
	selectUserZipCode,
	setHeaderServiceStoreInfo,
	setNearbyStores,
	setUserZipCode
} from '@state/data-access';
import { selectAllProductSkusPartNumber, selectResolvedSku } from './product-selection.selectors';
import { setEDDInfo, setResolvedSku, setSDDInfo } from './product-selection.actions';

import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngrx/store';
import { StoreDao } from '@shared/data-access/dao';
import { of, throwError } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { SameDayDeliveryService } from 'libs/pdp/data-access-new/services/src/lib/same-day-delivery.service';

@Injectable()
export class HeaderLocationEffects {
	setSDDInventory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setUserZipCode, setResolvedSku),
			concatLatestFrom(() => [
				this.store.select(selectResolvedSku),
				this.store.select(selectUserZipCode)
			]),
			switchMap(
				(
					info: [
						ReturnType<typeof setUserZipCode | typeof setResolvedSku>,
						ProductSku | null,
						string
					]
				) =>
					this.getSDDInfo(info).pipe(
						map((data) => setSDDInfo({ sdd: data.skuAvailability[0] })),
						catchError(() => of(setSDDInfo({ sdd: null })))
					)
			)
		)
	);

	getEDDInfo$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setResolvedSku),
			// Since we need cookies from document object for setSkuDeliveryDate API call, only run this on the browser
			filter(() => isPlatformBrowser(this.platformId)),
			switchMap((info) =>
				this.setSkuDeliveryDate(info).pipe(
					map((data) => setEDDInfo({ eddInfo: data })),
					catchError(() => of(setEDDInfo({ eddInfo: null })))
				)
			)
		)
	);

	getNearbyStores$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setHeaderServiceStoreInfo, changeHeaderServiceStoreInfo),
			concatLatestFrom(() => this.store.select(selectAllProductSkusPartNumber)),
			switchMap(
				(
					stateData: [
						ReturnType<
							typeof setHeaderServiceStoreInfo | typeof changeHeaderServiceStoreInfo
						>,
						string[]
					]
				) =>
					this.getStoreInfo(stateData).pipe(
						map((data) => setNearbyStores({ nearbyStores: data })),
						catchError(() => of(setNearbyStores({ nearbyStores: null })))
					)
			)
		)
	);
	sameDayDeliveryEnabled: boolean;
	skuEDDEnabled: boolean;

	constructor(
		private actions$: Actions,
		private store: Store,
		public sameDayDeliveryService: SameDayDeliveryService,
		private productService: ProductService,
		private utilityService: UtilityService,
		private chain: Chain,
		private killSwitchService: KillswitchService,
		private storeDao: StoreDao,
		private cookieService: CookieService,
		@Inject(PLATFORM_ID) private readonly platformId
	) {
		this.killSwitchService.getKillSwitchesEnvironment$().subscribe((data) => {
			this.sameDayDeliveryEnabled = this.utilityService.checkKillSwitch(
				'sameDayDeliveryEnabled',
				data,
				this.chain.chainIdentifierAbbr
			);

			this.skuEDDEnabled = this.utilityService.checkKillSwitch(
				'skuEDDEnabled',
				data,
				chain.chainIdentifierAbbr
			);
		});
	}

	public getSDDInfo(
		data: [ReturnType<typeof setUserZipCode | typeof setResolvedSku>, ProductSku | null, string]
	) {
		const resolvedSku = data[1];
		const zipCode = data[2];
		if (resolvedSku && zipCode && this.sameDayDeliveryEnabled) {
			return this.sameDayDeliveryService.getSameDayDeliveryApi$(
				zipCode,
				Number(resolvedSku.partNumber)
			);
		} else {
			return of(null);
		}
	}

	private setSkuDeliveryDate(info: ReturnType<typeof setResolvedSku>) {
		const hasDeliveryZipCookie =
			this.cookieService.check('deliveryZip') &&
			this.cookieService.get('deliveryZip') &&
			this.cookieService.get('deliveryZip') !== 'null';
		const hasWhereaboutsCookie =
			this.cookieService.check('whereabouts') &&
			this.cookieService.get('whereabouts') &&
			this.cookieService.get('whereabouts') !== 'null';
		const noZipCookieSet = !hasDeliveryZipCookie && !hasWhereaboutsCookie;
		if (noZipCookieSet) {
			return throwError(() => new Error('Missing Zip Code Cookie'));
		}

		if (info.resolvedSku && this.skuEDDEnabled) {
			return this.productService.getSkuDeliveryDate$(info.resolvedSku.partNumber);
		} else {
			return of(null);
		}
	}

	public getStoreInfo(
		info: [
			ReturnType<typeof setHeaderServiceStoreInfo | typeof changeHeaderServiceStoreInfo>,
			string[]
		]
	) {
		const zipcode = info[0]?.storeInfo?.zipcode
			? info[0].storeInfo.zipcode
			: info[0]?.storeInfo?.zip;
		if (!info[1] || info[1].length === 0) {
			return of(null);
		}
		return this.storeDao.getStoreInformation$(zipcode?.split('-')[0], info[1], 1, true, 1).pipe(
			map((response) => {
				let storeDataBySku = {};
				if (info[1]) {
					info[1].forEach((singleSku) => {
						storeDataBySku = {
							...storeDataBySku,
							[singleSku]: []
						};
						response.data.results.forEach((storeResult) => {
							const skuInventory = storeResult.skus.findIndex(
								(s) => s.sku === singleSku
							);
							const { store: storeRes, distance, units } = storeResult;
							if (
								parseInt(storeResult.skus[skuInventory].qty.ats, 10) > 0 ||
								parseInt(storeResult.skus[skuInventory].qty.bopl, 10) > 0 ||
								parseInt(storeResult.skus[skuInventory].qty.isa, 10) > 0
							) {
								storeDataBySku[singleSku].push({
									storeRes,
									distance,
									units,
									inventory: storeResult.skus[skuInventory].qty
								});
							}
						});
					});
					return storeDataBySku;
				}
			})
		);
	}
}
